<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import SecurityMeasuresRepository from '@repositories/securityMeasures.repository';
import { ISecurityMeasures } from '../../types/securityMeasures.type';
import CompaniesRepository from '@repositories/companies.repository';
import { ICompany } from '../../types/company.type';
import DataTable from '@components/DataTable.vue';

const isLoadingSecurityMeasures = ref<boolean>(false);

const store = Store();
const { $toast } = useToast();
const { securityMeasures: SecurityMeasuresPinia }: any = storeToRefs(store);
const securityMeasures = ref<ISecurityMeasures[]>([]);
const companies = ref<ICompany[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedSecurityMeasures = ref<ISecurityMeasures | null>(null);
const isViewActive = ref<boolean>(true);
const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Nombre',
  },

  {
    key: 'restore',
    label: 'Restaurar',
  },
]);
const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Nombre',
  },

  {
    key: 'description',
    label: 'Descripcion',
  },
  {
	key: 'company',
	label: 'Sponsor',
	formatter: (e: any) => e?.name || null,
  },
  {
    key: 'assetInfo',
    label: 'Editar',
  },
  {
    key: 'delete',
    label: 'Eliminar',
  },
]);
const fieldsFilter: any = ref([
  {
    key: 'search',
    title: 'Buscar',
    type: 'text',
    placeholder: 'Buscar por descripcion',
    value: null,
    cols: 3,
  },
  {
    key: 'companyId',
    title: 'Nombre del sponsor',
    type: 'select',
    label: 'name',
    placeholder: 'Buscar por sponsor',
    formatValue: (val: any) => val?.id ?? null,
    value: null,
    options: companies,
    cols: 3,
  },
]);
const searchFormRef = ref<any>(null);

// API
const getSecurityMeasuresFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingSecurityMeasures.value = true;
  try {
    const data = await SecurityMeasuresRepository.getSecurityMeasures({
      page,
      deleted,
      ...params,
    });

    if (data && Array.isArray(data?.securityMeasures)) {
      paginationData.value = data.pagination;
      updateSecurityMeasures(data.securityMeasures);
      return data.securityMeasures;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching SecurityMeasures:', error);
    return [];
  } finally {
    isLoadingSecurityMeasures.value = false;
  }
};

const getCompaniesFromApi = async () => {
  const data = await CompaniesRepository.getCompanies();
  companies.value = data.companies;
};

const updateSecurityMeasures = (newSecurityMeasures: ISecurityMeasures[]) => {
  securityMeasures.value = newSecurityMeasures;
  store.setProp({ key: 'securityMeasures', value: newSecurityMeasures });
};

const loadSecurityMeasures = async () => {
  await getSecurityMeasuresFromAPI(1);
  await getCompaniesFromApi();
};

onMounted(() => {
  loadSecurityMeasures();
});

// Create inputs from modals
const fieldsInputSecurityMeasures = computed(() => {
  return [
    {
      key: 'name',
      title: 'Nombre',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Nombre de la medida de seguridad',
      value: selectedSecurityMeasures.value?.name,
      cols: 12,
    },
    {
      key: 'description',
      title: 'Descripción',
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: 'Descripcion de la medida de seguridad',
      value: selectedSecurityMeasures.value?.description,
      cols: 12,
    },
    {
      key: 'companyId',
      label: 'name',
      selectLabel: null,
      value: selectedSecurityMeasures.value?.company.id,
      searchable: true,
      title: 'Sponsor',
      trackBy: 'id',
      type: 'select',
      placeholder: 'Nombre del sponsor',
      formatValue: (val: any) => val.id,
      cols: 12,
      options: companies,
    },

  ];
});

// UI
const onChangePage = async (page: number) => {
  getSecurityMeasuresFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = 'Agregar medida de seguridad';
  isModalOpen.value = true;
  modalType.value = 'modalCreateSecurityMeasures';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedSecurityMeasures.value = null;
};

const handleActive = async () => {
  const securityMeasures = await getSecurityMeasuresFromAPI(1);
  updateSecurityMeasures(securityMeasures);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const securityMeasuresDeleted = await getSecurityMeasuresFromAPI(1, true);
  updateSecurityMeasures(securityMeasuresDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ISecurityMeasures) => {
  modalTitle.value = isViewActive.value
    ? `Borrar la medida de seguridad con id: ${item.id}`
    : `Restaurar la medida de seguridad con id: ${item.id}`;
  modalType.value = 'modalDialog';
  selectedSecurityMeasures.value = item;
  isModalOpen.value = true;
};

// Filter Functions

const handleOnSubmit = async ({ items }: any) => {
  getSecurityMeasuresFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getSecurityMeasuresFromAPI(1, false);
};

//CRUD
const handleCreateSecurityMeasures = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const data = await SecurityMeasuresRepository.createSecurityMeasure(items);
	store.setProp({
		key: 'securityMeasures',
		value: [...securityMeasures.value, data],
    });
	securityMeasures.value = [...securityMeasures.value, data];

    $toast?.success(`Medida de seguridad agregado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar crear');
  }

  handleClose();
};

const handleSelectSecurityMeasures = (securityMeasuresData: ISecurityMeasures) => {
  modalTitle.value = 'Actualizar Medida de seguridad';
  (isModalOpen.value = true), (modalType.value = 'modalUpdateSecurityMeasures');
  selectedSecurityMeasures.value = securityMeasuresData;
};

const handleUpdateSecurityMeasures = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedSecurityMeasures.value) return;
  const assetForUpdate = {
    ...items,
    id: selectedSecurityMeasures.value.id,
  };

  try {
    const data =
      await SecurityMeasuresRepository.updateSecurityMeasure(assetForUpdate);
    let index = store.securityMeasures.findIndex(
      (value: any) => value.id === data.id,
    );
    if (index > -1) {
      store.setProp({ key: 'securityMeasures', index, value: data });
    }
    $toast?.success(`Medida de seguridad actualizado con exito`);
  } catch (error) {
    $toast?.error('Ocurrió un error al intentar actualizar');
  }

  handleClose();
};

const handleDeleteRestoreSecurityMeasures = async () => {
  if (selectedSecurityMeasures?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await SecurityMeasuresRepository.deleteSecurityMeasure(
          selectedSecurityMeasures.value.id,
        );
      } else {
        response = await SecurityMeasuresRepository.restoreSecurityMeasure(
          selectedSecurityMeasures.value.id,
        );
      }

      if (response === 200) {
        store.deleteItem({
          key: 'securityMeasures',
          value: selectedSecurityMeasures?.value?.id,
        });
      }
    } catch (error) {
      if (isViewActive.value) {
        return $toast?.error('Error al intentar eliminar');
      }
      $toast?.error('Error al intentar restaurar');
    }

    handleClose();
  }
};
</script>

<template>
  <Layout title="Medidas de seguridad" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>Activos</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>Inactivos</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content> Agregar medida de seguridad </template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingSecurityMeasures"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="SecurityMeasuresPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(assetInfo)="assetData">
          <div
            @click="handleSelectSecurityMeasures(assetData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateSecurityMeasures'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputSecurityMeasures"
            @onSubmit="handleCreateSecurityMeasures"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Agregar medida de seguridad</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateSecurityMeasures'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputSecurityMeasures"
            @onSubmit="handleUpdateSecurityMeasures"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>Actualizar</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `¿Está seguro que desea borrar la medida de seguridad ${selectedSecurityMeasures?.description}?`
                : `¿Está seguro que desea restaurar la medida de seguridad ${selectedSecurityMeasures?.description}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreSecurityMeasures"
            >
              <template #content>
                <div>
                  <span>Confirmar</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
